import React, { useContext } from "react";

import { compareDesc } from "date-fns";
import { useTranslation } from "react-i18next";
import { isValidPhoneNumber } from "react-phone-number-input";
import isEmail from "validator/lib/isEmail";
import * as yup from "yup";

import ActionButtons from "../../../../../../components/Generic/ActionButtons";
import AddressComponent from "../../../../../../components/Generic/AddressComponent";
import GeneralCommentsComponent from "../../../../../../components/Generic/GeneralComments";
import LoadingModal from "../../../../../../components/LoadingModal";
import { ExternalList } from "../../../../../../context/MovingResources";
import { QuoteContext } from "../../../../../../context/QuoteContext";
import { AutomaticSequencing } from "../../../../../../models/automatic.sequencing";
import { GeneralComments } from "../../../../../../models/general.comments";
import { CustomerLocations, QuoteComment } from "../../../../../../models/sales";
import Form from "./Form";
import useStyles from "./styles";

type Props = {
  customerDestinationErrors?: yup.ValidationError[];
  customerOriginErrors?: yup.ValidationError[];
  errors?: yup.ValidationError[];
  externalList?: ExternalList;
  isLoading?: boolean;
  isOnViewMode?: boolean;
  isSavingAndSending?: boolean;
  isUpdating: boolean;
  onCancel?: () => void;
  onSave?: () => void;
  onSaveAndSend?: () => void;
  sequence?: AutomaticSequencing;
  updateQuote: (attribute: string, value: any, noChanges?: boolean) => void;
};

const QuoteMaintenanceDetails = ({
  customerDestinationErrors,
  customerOriginErrors,
  errors,
  externalList,
  isLoading = false,
  isOnViewMode = false,
  isSavingAndSending = false,
  isUpdating,
  onCancel,
  onSave,
  onSaveAndSend,
  updateQuote,
}: Props) => {
  const { t: translate } = useTranslation();
  const { quote, setQuote } = useContext(QuoteContext);

  const classes = useStyles(isOnViewMode)();

  const checkIfDestinationOrOriginCustomerIsEmpty = (type: "destination" | "origin") => {
    const fieldsNotExists: boolean =
      !quote?.[type]?.customer?.name &&
      !quote?.[type]?.customer?.email &&
      !quote?.[type]?.customer?.phone;

    const fieldsAreEmpty: boolean =
      quote?.[type]?.customer?.name === "" ||
      quote?.[type]?.customer?.email === "" ||
      quote?.[type]?.customer?.phone === "";

    const fieldValidated =
      isEmail(quote?.[type]?.customer?.email ?? "") &&
      isValidPhoneNumber(quote?.[type]?.customer?.phone ?? "");

    if (fieldsNotExists || (fieldsAreEmpty && fieldValidated)) return true;

    return false;
  };

  const checkIfCustomerIsBeenCreated = (
    type: "destination" | "origin",
    originCustomerId?: number
  ): boolean => {
    // if pickup customer and delivery customer were chose and are the same;
    if (originCustomerId && quote?.[type]?.customer?.id) {
      return originCustomerId === quote?.[type]?.customer?.id;
    }

    // if pickup customer or delivery customer is been created
    if (originCustomerId && !checkIfDestinationOrOriginCustomerIsEmpty(type)) return false;

    return true;
  };

  const handleAddComment = (comment: GeneralComments) => {
    const clonnedComments = quote?.quoteComments || [];
    const newComment = {
      ...comment,
      jobId: quote?.jobId,
    } as QuoteComment;

    clonnedComments.unshift(newComment);

    setQuote({ ...quote, quoteComments: clonnedComments });
  };

  return (
    <div data-cy="details-box">
      <div className={classes.container}>
        <Form quote={quote} errors={errors} update={updateQuote} externalList={externalList} />

        <div className={classes.addressComponentMargin} />

        <AddressComponent
          title={translate("pickup")}
          access={quote?.pickupAccess}
          inventory={quote?.inventory || []}
          customerLocations={quote?.origin || ({} as CustomerLocations)}
          update={(value: CustomerLocations) => {
            if (checkIfCustomerIsBeenCreated("destination", value?.customer?.id)) {
              updateQuote("destination", {
                customer: value?.customer,
                contacts: quote?.destination?.contacts,
                locations: quote?.destination?.locations,
              });
            }
            updateQuote("origin", value);
          }}
          updateAll={(inventory, customerLocation) =>
            setQuote({
              ...quote,
              inventory,
              origin: customerLocation,
            })
          }
          onCopyInfo={() =>
            updateQuote("origin", {
              customer: quote?.destination?.customer,
              locations: quote?.origin?.locations,
              contacts: quote?.origin?.contacts,
            })
          }
          externalList={externalList}
          isLoading={isLoading}
          externalErrors={customerOriginErrors}
        />
        <AddressComponent
          title={translate("delivery")}
          access={quote?.deliveryAccess}
          inventory={quote?.inventory || []}
          customerLocations={quote?.destination || ({} as CustomerLocations)}
          update={(value: CustomerLocations) => {
            updateQuote("all", {
              ...quote,
              destination: value,
              origin: {
                ...quote?.origin,
                customer: {
                  ...quote?.origin?.customer,
                  destinationMobile: value.customer.destinationMobile,
                },
              },
            });
          }}
          updateAll={(inventory, customerLocation) => {
            setQuote({
              ...quote,
              inventory,
              destination: customerLocation,
            });
          }}
          onCopyInfo={() => {
            updateQuote("destination", {
              customer: quote?.origin?.customer,
              locations: quote?.destination?.locations,
              contacts: quote?.origin?.contacts,
            });
          }}
          externalList={externalList}
          isLoading={isLoading}
          externalErrors={customerDestinationErrors}
        />

        <GeneralCommentsComponent
          comments={quote?.quoteComments?.sort((a, b) =>
            compareDesc(new Date(a.createdAt ?? ""), new Date(b.createdAt ?? ""))
          )}
          onAddComment={handleAddComment}
          onRemoveComment={(comments: QuoteComment[]) =>
            setQuote({ ...quote, quoteComments: comments })
          }
        />

        {!isOnViewMode && (
          <div className={classes.buttons}>
            <ActionButtons
              showSaveAndSendButton={isSavingAndSending}
              onSaveAndSend={onSaveAndSend}
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              onSave={onSave}
              onCancel={onCancel}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default QuoteMaintenanceDetails;
